import { prontoProductsUrl } from '../../utils/ConstantUtils'
import { updateTokenUsageTime } from '../../utils/storageUtils'
import { getApiInstance } from '../axiosInterceptor'
import { generateProntoToken } from '../prontoService/prontoServiceUtil'

/**
 * Handles the fetching of product data from Pronto API
 * @param {object} instance - MSAL instance for authentication
 * @param {array} data - Array of values (keycodes or APNs) to search for
 * @param {string} searchType - Type of search ('keycode' or 'apn')
 * @param {boolean} isCognitoUser - Flag indicating if the user is authenticated with Cognito
 * @returns {Promise<object>} - API response with product information
 */
export const handleProntoProductsUtil = async (instance, data, searchType = 'apn', isCognitoUser = false, storeId = null) => {
  try {
    if (!instance || !data || !Array.isArray(data)) {
      throw new Error('Invalid parameters provided to handleProntoProductsUtil')
    }

    // Pass the isCognitoUser flag to generateProntoToken
    const token = await generateProntoToken(instance, isCognitoUser)
    if (!token) {
      throw new Error('Failed to generate Pronto token')
    }

    const productInfo = await fetchProntoProducts(instance, token, data, searchType, storeId)

    if (!productInfo) {
      throw new Error('Failed to fetch product information')
    }

    if (!productInfo.data || !Array.isArray(productInfo.data.sohInfo)) {
      throw new Error('Invalid product information format received')
    }

    return {
      status: productInfo.status,
      data: {
        sohInfo: productInfo.data.sohInfo
      }
    }
  } catch (error) {
    console.error('Error in handleProntoProductsUtil:', error)
    return {
      status: error.response?.status || 500,
      data: {
        sohInfo: []
      }
    }
  }
}

const fetchProntoProducts = async (instance, token, data, searchType, storeId = null) => {
  try {
    if (!data.length) {
      return null
    }

    const payload = {
      [searchType]: data.map(value => Number(value)),
      requestType: 'soh'
    }

    const requestHeaders = { 'X-Pronto-Token': token }
    if (storeId) {
      requestHeaders['StoreId'] = String(storeId)
    }

    const response = await getApiInstance(instance).post(
      prontoProductsUrl,
      payload,
      {
        headers: requestHeaders
      }
    )

    if (response.status === 200) {
      updateTokenUsageTime()
      return response
    }

    throw new Error(`Unexpected response status: ${response.status}`)
  } catch (error) {
    console.error('Error in fetchProntoProducts:', error)
    if (error.response) {
      console.error('Error Response Data:', error.response.data)
      return error.response
    }
    return null
  }
}